import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Alert, Button, Group, Loader, Modal, Paper, Stack, Table, Text, Title } from '@mantine/core'
import type { ConversaEmPausa } from '@/api/whatsappGateways'
import { useOrganizacoesWhatsApi } from '@/hooks/useEscopoWhatsApi'
import { useConversasEmPausa, useDevolverConversa } from '@/hooks/useWhatsappGateways'
import { mensagemDeErro, motivoDoTransbordo } from './mensagens'

/** "3 h 12 min" / "8 min" / "menos de 1 min" até `pausadoAte`, contado no cliente. */
export function tempoAteVoltar(pausadoAte: string, agora: number): string {
  const min = Math.floor((new Date(pausadoAte).getTime() - agora) / 60_000)
  if (min < 1) return 'menos de 1 min'
  const h = Math.floor(min / 60)
  return h > 0 ? `${h} h ${min % 60} min` : `${min} min`
}

function diaEHora(iso: string): string {
  const d = new Date(iso)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(d.getDate())}/${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`
}

/**
 * "Atendimento humano" (spec 2026-11-02 §5.3): as conversas em que o bot fez transbordo e
 * está pausado, com a ação de devolver ao bot antes do fim da pausa. Aparece para quem tem
 * `whatsapi:atender` na organização (`pode.atender` de GET /v1/whatsapi/organizacoes, spec
 * conversas §5.6); some também quando o gateway não tem bot (`tem_bot` da lista). WhatsApi
 * anterior à rota (`disponivel: false`) mostra a seção dizendo que é indisponível.
 */
export function AtendimentoHumanoDoGateway({ orgId, gwId }: { orgId: number; gwId: string }) {
  const orgs = useOrganizacoesWhatsApi()
  const podeAtender = !!orgs.data?.find((o) => o.id === orgId)?.pode.atender
  return podeAtender ? <ConversasEmPausaDoGateway orgId={orgId} gwId={gwId} /> : null
}

function ConversasEmPausaDoGateway({ orgId, gwId }: { orgId: number; gwId: string }) {
  const conversas = useConversasEmPausa(orgId, gwId)
  const devolver = useDevolverConversa(orgId, gwId)
  const [confirmar, setConfirmar] = useState<ConversaEmPausa | null>(null)
  const [agora, setAgora] = useState(() => Date.now())
  useEffect(() => {
    const t = setInterval(() => setAgora(Date.now()), 30_000)
    return () => clearInterval(t)
  }, [])

  const lista = conversas.data
  if (lista?.disponivel && !lista.tem_bot) return null
  return (
    <Paper withBorder p="sm" radius="md">
      <Stack gap="xs">
        <Title order={5}>Atendimento humano</Title>
        {conversas.isLoading && <Loader size="sm" />}
        {conversas.isError && <Alert color="red" variant="light">{mensagemDeErro(conversas.error)}</Alert>}
        {lista && !lista.disponivel && <Text size="sm" c="dimmed">Indisponível nesta versão do WhatsApi.</Text>}
        {lista?.disponivel && lista.items.length === 0 && (
          <Text size="sm" c="dimmed">
            Nenhuma conversa em atendimento humano. O bot está respondendo todos os números liberados.
          </Text>
        )}
        {lista?.disponivel && lista.items.length > 0 && (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Número</Table.Th>
                <Table.Th>Motivo</Table.Th>
                <Table.Th>Desde</Table.Th>
                <Table.Th>Volta ao bot em</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {lista.items.map((c) => (
                <Table.Tr key={c.ref}>
                  <Table.Td><Text ff="monospace" size="sm">{c.numero}</Text></Table.Td>
                  <Table.Td>{motivoDoTransbordo(c.motivo)}</Table.Td>
                  <Table.Td>{diaEHora(c.desde)}</Table.Td>
                  <Table.Td>{tempoAteVoltar(c.pausado_ate, agora)}</Table.Td>
                  <Table.Td>
                    <Group gap="xs" wrap="nowrap">
                      <Button size="xs" variant="subtle" component={Link}
                        to={`/whatsapi/conversas?org=${orgId}&gateway=${encodeURIComponent(gwId)}&ref=${c.ref}`}>
                        Abrir em Conversas
                      </Button>
                      <Button size="xs" variant="light" onClick={() => setConfirmar(c)}>Devolver ao bot</Button>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>
      <Modal opened={confirmar != null} onClose={() => setConfirmar(null)} title="Devolver a conversa ao bot?">
        <Stack>
          <Text size="sm">
            O bot volta a responder {confirmar?.numero}. Se alguém da equipe estiver conversando com ele agora, o bot passa a responder junto.
          </Text>
          <Group>
            <Button variant="subtle" onClick={() => setConfirmar(null)}>Cancelar</Button>
            <Button loading={devolver.isPending}
              onClick={() => confirmar && devolver.mutate(confirmar.ref, { onSettled: () => setConfirmar(null) })}>
              Confirmar
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  )
}
